import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Upload,
  Linkedin,
  Globe,
  Github,
  Twitter,
  Loader2,
  ArrowRight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getJobs, createApplication } from "../services/api";

const defaultFieldRequirements = {
  phone: false,
  resume: false,
  coverLetter: false,
  linkedin: false,
  portfolio: false,
  github: false,
  twitter: false,
};

const Field = ({ label, required, children, icon: Icon }) => (
  <div>
    <label className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs text-bone/80 mb-2 flex items-center gap-2">
      {Icon && <Icon size={12} />}
      {label} {required && <span className="text-red">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  "w-full bg-transparent border-4 border-bone rounded-none px-4 py-3 font-mono text-base text-bone placeholder:text-bone/40 brut-focus";

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: "",
    email: "",
    phone: "",
    coverLetter: "",
    resumeFile: null,
    linkedin: "",
    portfolio: "",
    github: "",
    twitter: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchJobs = async (retryCount = 0) => {
      try {
        const data = await getJobs("active");
        setJobs(data || []);
        setLoading(false);
      } catch (error) {
        if (!error.response && retryCount < 2) {
          setTimeout(() => fetchJobs(retryCount + 1), 1000);
          return;
        }
        setJobs([]);
        setLoading(false);
        if (error.response) {
          toast.error("Failed to load jobs");
        }
      }
    };
    fetchJobs();
  }, []);

  const getRequirements = (job) => {
    if (Array.isArray(job.requirements)) return job.requirements;
    return job.requirements ? job.requirements.split("\n").filter((r) => r.trim()) : [];
  };

  const getFieldRequirements = (job) => job.fieldRequirements || defaultFieldRequirements;

  const handleApply = (job) => {
    setSelectedJob(job);
    setApplicationData({
      name: "",
      email: "",
      phone: "",
      coverLetter: "",
      resumeFile: null,
      linkedin: "",
      portfolio: "",
      github: "",
      twitter: "",
    });
    setIsApplyModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setApplicationData((prev) => ({ ...prev, resumeFile: file }));
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    const fieldReqs = getFieldRequirements(selectedJob);

    if (!applicationData.name || !applicationData.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!applicationData.phone) {
      toast.error("Phone number is required");
      return;
    }
    if (fieldReqs.resume && !applicationData.resumeFile) {
      toast.error("Resume is required");
      return;
    }
    if (fieldReqs.coverLetter && !applicationData.coverLetter) {
      toast.error("Cover letter is required");
      return;
    }
    if (fieldReqs.linkedin && !applicationData.linkedin) {
      toast.error("LinkedIn profile is required");
      return;
    }
    if (fieldReqs.portfolio && !applicationData.portfolio) {
      toast.error("Portfolio link is required");
      return;
    }

    setIsSubmitting(true);
    try {
      await createApplication({
        jobId: selectedJob.id,
        jobTitle: selectedJob.title,
        name: applicationData.name,
        email: applicationData.email,
        phone: applicationData.phone || "Not provided",
        coverLetter: applicationData.coverLetter || "No cover letter provided",
        linkedin: applicationData.linkedin || "",
        portfolio: applicationData.portfolio || "",
        github: applicationData.github || "",
        twitter: applicationData.twitter || "",
      }, applicationData.resumeFile);  // Pass the actual file

      toast.success(`Application in. We'll holler back.`);
      setIsApplyModalOpen(false);
    } catch (error) {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red animate-spin" />
      </div>
    );
  }

  const fieldReqs = selectedJob ? getFieldRequirements(selectedJob) : defaultFieldRequirements;

  return (
    <div data-testid="page-jobs" className="min-h-screen bg-navy text-bone">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-navy text-bone border-b-2 border-red">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 md:py-8 flex items-center justify-between">
          <Link
            to="/"
            data-testid="jobs-home-link"
            className="flex items-center gap-3 group"
            aria-label="Home"
          >
            <ArrowLeft
              size={18}
              className="text-bone/70 group-hover:text-red group-hover:-translate-x-0.5 transition-all"
            />
            <img
              src="/assets/frdm-logo.png"
              alt="FRDM"
              className="h-12 sm:h-14 md:h-20 lg:h-24 w-auto"
              style={{ filter: "invert(1) brightness(1.05)" }}
            />
          </Link>
          <span className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-bone/70">
            ◢ JOBS
          </span>
        </div>
      </header>

      {/* Job listings */}
      <main className="bg-bone text-navy py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12">
          <div className="flex justify-between items-end mb-10 md:mb-14">
            <h2 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
              Open <span className="text-red">roles.</span>
            </h2>
            <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-navy/60">
              § DIRECTORY
            </p>
          </div>

          {jobs.length === 0 ? (
            <div
              data-testid="jobs-empty-state"
              className="border-4 border-navy py-20 px-8 text-center"
            >
              <p className="font-display uppercase tracking-tight text-4xl md:text-6xl leading-none">
                Nothing open <span className="text-red">right now.</span>
              </p>
              <p className="font-mono uppercase tracking-[0.25em] text-xs text-navy/60 mt-6">
                ◢ CHECK BACK SOON · OR DROP US A LINE
              </p>
            </div>
          ) : (
            <ul className="space-y-6 md:space-y-8">
              {jobs.map((job, i) => (
                <li
                  key={job.id}
                  data-testid={`job-card-${job.id}`}
                  className="group bg-bone border-4 border-navy hover:bg-navy hover:text-bone transition-colors"
                >
                  <div className="p-6 md:p-10 grid grid-cols-12 gap-6">
                    <div className="col-span-12 md:col-span-8">
                      <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red mb-3">
                        ◢ NO. 0{i + 1} · {job.department || "TEAM"}
                      </p>
                      <h3 className="font-display uppercase leading-none tracking-tight text-4xl md:text-6xl">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 font-mono uppercase tracking-[0.2em] text-[11px] md:text-xs">
                        <span>◢ {job.location}</span>
                        <span>◢ {job.type}</span>
                        {job.department && <span>◢ {job.department}</span>}
                      </div>
                      <p className="font-body text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
                        {job.description}
                      </p>
                      {getRequirements(job).length > 0 && (
                        <div className="mt-6">
                          <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red mb-3">
                            § REQUIREMENTS
                          </p>
                          <ul className="space-y-1.5 font-body text-sm md:text-base">
                            {getRequirements(job).slice(0, 4).map((req, idx) => (
                              <li key={idx} className="flex gap-3">
                                <span className="text-red font-mono">◢</span>
                                <span>{req}</span>
                              </li>
                            ))}
                            {getRequirements(job).length > 4 && (
                              <li className="font-mono uppercase tracking-widest text-[10px] opacity-60 pl-6">
                                +{getRequirements(job).length - 4} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end md:items-end">
                      <button
                        onClick={() => handleApply(job)}
                        data-testid={`apply-button-${job.id}`}
                        className="bg-red text-bone hover:bg-bone hover:text-red border-4 border-navy group-hover:border-bone transition-colors font-display uppercase tracking-wider text-xl md:text-2xl px-7 py-4 inline-flex items-center gap-3"
                      >
                        Apply <ArrowRight size={22} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {/* Application Modal */}
      <Dialog open={isApplyModalOpen} onOpenChange={setIsApplyModalOpen}>
        <DialogContent
          data-testid="apply-modal"
          className="bg-navy border-4 border-red rounded-none max-w-xl max-h-[90vh] overflow-y-auto p-0 text-bone"
        >
          <div className="sticky top-0 bg-navy border-b-2 border-bone/20 z-10 px-6 py-5 flex items-start justify-between">
            <DialogHeader className="space-y-1 text-left">
              <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red">
                ◢ APPLY
              </p>
              <DialogTitle className="font-display uppercase tracking-tight leading-none text-3xl md:text-4xl text-bone">
                {selectedJob?.title}
              </DialogTitle>
              <DialogDescription className="font-mono uppercase tracking-[0.2em] text-[10px] text-bone/60 pt-1">
                ◢ {selectedJob?.location} · {selectedJob?.type}
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setIsApplyModalOpen(false)}
              aria-label="Close"
              className="text-bone hover:text-red p-1 -mr-1"
            >
              <X size={22} />
            </button>
          </div>

          {selectedJob && (
            <form
              onSubmit={handleSubmitApplication}
              data-testid="apply-form"
              className="px-6 py-6 space-y-5"
            >
              <Field label="Full Name" required>
                <input
                  type="text"
                  name="name"
                  value={applicationData.name}
                  onChange={handleInputChange}
                  placeholder="John Adams"
                  data-testid="apply-name-input"
                  className={inputClass}
                  required
                />
              </Field>

              <Field label="Email" required>
                <input
                  type="email"
                  name="email"
                  value={applicationData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  data-testid="apply-email-input"
                  className={inputClass}
                  required
                />
              </Field>

              <Field label="Phone" required>
                <input
                  type="tel"
                  name="phone"
                  value={applicationData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  data-testid="apply-phone-input"
                  className={inputClass}
                  required
                />
              </Field>

              <Field label="Resume">
                <label
                  htmlFor="resume"
                  className="block border-4 border-dashed border-bone p-6 text-center cursor-pointer hover:border-red transition-colors"
                >
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="apply-resume-input"
                  />
                  <Upload className="w-7 h-7 mx-auto mb-2" />
                  {applicationData.resumeFile ? (
                    <p className="font-mono text-sm text-red">
                      {applicationData.resumeFile.name}
                    </p>
                  ) : (
                    <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-bone/60">
                      Click to upload · PDF / DOC / DOCX
                    </p>
                  )}
                </label>
              </Field>

              {(fieldReqs.linkedin || fieldReqs.portfolio || fieldReqs.github || fieldReqs.twitter) && (
                <div className="space-y-4 pt-2">
                  <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-red border-t-2 border-bone/20 pt-4">
                    § LINKS
                  </p>
                  {fieldReqs.linkedin && (
                    <Field label="LinkedIn" required icon={Linkedin}>
                      <input
                        type="url"
                        name="linkedin"
                        value={applicationData.linkedin}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/in/you"
                        className={inputClass}
                      />
                    </Field>
                  )}
                  {fieldReqs.portfolio && (
                    <Field label="Portfolio" required icon={Globe}>
                      <input
                        type="url"
                        name="portfolio"
                        value={applicationData.portfolio}
                        onChange={handleInputChange}
                        placeholder="https://yoursite.com"
                        className={inputClass}
                      />
                    </Field>
                  )}
                  {fieldReqs.github && (
                    <Field label="GitHub" required icon={Github}>
                      <input
                        type="url"
                        name="github"
                        value={applicationData.github}
                        onChange={handleInputChange}
                        placeholder="https://github.com/you"
                        className={inputClass}
                      />
                    </Field>
                  )}
                  {fieldReqs.twitter && (
                    <Field label="Twitter / X" required icon={Twitter}>
                      <input
                        type="url"
                        name="twitter"
                        value={applicationData.twitter}
                        onChange={handleInputChange}
                        placeholder="https://x.com/you"
                        className={inputClass}
                      />
                    </Field>
                  )}
                </div>
              )}

              {fieldReqs.coverLetter && (
                <Field label="Cover Letter" required>
                  <textarea
                    name="coverLetter"
                    value={applicationData.coverLetter}
                    onChange={handleInputChange}
                    rows={5}
                    placeholder="Tell us why."
                    data-testid="apply-cover-input"
                    className={`${inputClass} resize-none`}
                  />
                </Field>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  className="flex-1 border-4 border-bone bg-transparent text-bone hover:bg-bone hover:text-navy transition-colors font-display uppercase tracking-wider text-base md:text-lg px-5 py-3"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="apply-submit-button"
                  className="flex-1 bg-red text-bone hover:bg-bone hover:text-red border-4 border-bone transition-colors font-display uppercase tracking-wider text-base md:text-lg px-5 py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Submit <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-navy text-bone border-t-2 border-bone/20">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-bone/70">
            ◢ HQ · 1850 POLK ST · SAN FRANCISCO · CA
          </p>
          <div className="flex gap-5 font-mono uppercase tracking-[0.25em] text-[10px] text-bone/70">
            <Link to="/" className="hover:text-red">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-red">
              Privacy
            </Link>
            <span>© 2026 FRDM</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Jobs;
